import React, { useContext } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Grid } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import InfoPopup from '~/components/InfoPopup'
import { CoolFarmCropOptionsContext } from '../..'

const WasteWater: React.FC = () => {
  const { t } = useTranslation()
  const { control, setValue } = useFormContext()

  const hasWasteWater = useWatch({ control, name: 'hasWasteWater' })
  const options = useContext(CoolFarmCropOptionsContext)

  const volumeUnitOptions = options?.wasteWaterVolumeUnits?.map(type => ({
    key: type.key,
    value: type.key,
    text: type.label
  })) || []

  const oxygenDemandUnitOptions = options?.wasteWaterOxygenDemandUnits?.map(type => ({
    key: type.key,
    value: type.key,
    text: type.label
  })) || []

  const oxygenDemandTypeOptions = options?.wasteWaterOxygenTypes?.map(type => ({
    key: type.key,
    value: type.key,
    text: type.label
  })) || []

  const treatmentProcessOptions = options?.wasteWaterTreatments?.map(type => ({
    key: type.key,
    value: type.key,
    text: type.label
  })) || []

  return (
    <Grid>
      <Grid.Row>
        <Grid.Column width={16}>
          <p style={{ marginBottom: '1em', color: '#666' }}>
            {t('You can skip this section if pools of standing waste water or reservoirs are not managed in this crop, or if the average annual temperature of your farm is lower than 15°C as no emissions are calculated, and it will not impact the overall emission results.')}
          </p>
        </Grid.Column>
      </Grid.Row>

      <Grid.Row>
        <Grid.Column width={16}>
          <FormFields.Checkbox
            control={control}
            name={'hasWasteWater'}
            label={t('Tick this box and add details ONLY if you produce waste water containing organic matter?')}
            defaultValue={0}
            checkboxValue={1}
            onChange={(e, data) => setValue('hasWasteWater', data.checked)}
          />
        </Grid.Column>
      </Grid.Row>

      {Boolean(hasWasteWater) && (
        <>
          <Grid.Row>
            <Grid.Column width={8}>
              <FormFields.Input
                control={control}
                name='wasteWaterVolume'
                label={t('Waste water volume')}
                placeholder={t('Enter volume')}
                typeInput='controled'
                rules={{ required: true }}
                noErrorMessage
                withoutAsterisk
              />
            </Grid.Column>
            <Grid.Column width={8}>
              <FormFields.Select
                control={control}
                name='wasteWaterVolumeUnit'
                placeholder={t('Select volume unit')}
                label={t('Waste water unit')}
                options={volumeUnitOptions}
                rules={{ required: true }}
                noErrorMessage
                withoutAsterisk
              />
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={8}>
              <FormFields.Input
                control={control}
                name='wasteWaterOxygenDemand'
                label={
                  <div style={{ display: 'flex', gap: 5 }}>
                    <div>{t('Oxygen demand')}</div>
                    <InfoPopup
                      content={<div>
                        <p>{t('If unknown, the following IPCC defaults for chemical oxygen demand may be useful:')}</p>
                        <div>{t('Process	COD')}</div>
                        <div>{t('Alcohol refining	7000 mg/L')}</div>
                        <div>{t('Beer & Malt	2900 mg/L')}</div>
                        <div>{t('Coffee	9000 mg/L')}</div>
                        <div>{t('Dairy products	2700 mg/L')}</div>
                        <div>{t(' Fish processing	2500 mg/L')}</div>
                        <div>{t('Sugar refining	3200 mg/L')}</div>
                        <div>{t('Wine & vinegar	1500 mg/L')}</div>
                      </div>}
                      styleIcon={{ fontSize: '1.2em' }}
                    />
                  </div>
                }
                placeholder={t('Enter oxygen demand')}
                typeInput='controled'
                rules={{ required: true }}
                noErrorMessage
                withoutAsterisk
              />
            </Grid.Column>
            <Grid.Column width={8}>
              <FormFields.Select
                control={control}
                name='wasteWaterOxygenDemandUnit'
                label={t('Unit')}
                placeholder={t('Select unit')}
                options={oxygenDemandUnitOptions}
                rules={{ required: true }}
                noErrorMessage
                withoutAsterisk
              />
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={8}>
              <FormFields.Select
                control={control}
                name='wasteWaterOxygenDemandType'
                label={t('Oxygen demand type')}
                placeholder={t('Select oxygen demand type')}
                options={oxygenDemandTypeOptions}
                rules={{ required: true }}
                noErrorMessage
                withoutAsterisk
              />
            </Grid.Column>
            <Grid.Column width={8}>
              <FormFields.Select
                control={control}
                placeholder={t('Select treatment proccess')}
                name='wasteWaterTreatmentProcess'
                label={t('Treatment process')}
                options={treatmentProcessOptions}
                rules={{ required: true }}
                noErrorMessage
                withoutAsterisk
              />
            </Grid.Column>
          </Grid.Row>
        </>
      )}
    </Grid>
  )
}

export default WasteWater
